import {useCallback, useRef, useState, useSyncExternalStore} from 'react';
import {
  initialTurnState,
  reduceTurn,
  type TurnAction,
  type TurnState,
} from './turn-reducer';
import type {ChatMessage, ChatStreamEvent} from '../../hooks/chat/types';
import type {StreamBufferProgress} from './use-stream-recovery';
import {
  ToolCallStreamAccumulator,
  type ToolCallStreamState,
} from './tool-call-stream-accumulator';

export interface ToolCallResultPayload {
  toolCallId: string;
  toolName: string;
  result: string | null;
}

export interface ToolCallExecutePayload {
  toolCallId: string;
  toolName: string;
  arguments?: Record<string, unknown> | null;
}

/** The terminal event that ended a turn. */
export type StreamEndReason = 'DONE' | 'ERROR' | 'CANCELLED';

export interface UseTurnOrchestratorOptions {
  /** Messages to initialize with on first render (avoids empty-state flash on navigation). */
  initialMessages?: ChatMessage[];
  initialChatId?: string;
  /** Fires once per stream when the first meaningful content arrives (text or tool call). */
  onStreamStart?: () => void;
  /** Fires when the server signals the stream is terminal (DONE, ERROR or CANCELLED). Not invoked for caller-initiated abort/reset. */
  onStreamEnd?: (reason: StreamEndReason) => void;
  onToolStreaming?: (state: ToolCallStreamState) => void;
  onToolExecute?: (payload: ToolCallExecutePayload) => void;
  onToolResult?: (payload: ToolCallResultPayload) => void;
  /**
   * Event types to skip processing. The seq cursor still advances past them
   * (preserving seq ordering), so upstream events downstream of the skipped
   * ones still flow through. Useful when a caller wants the transport to
   * stay in sync but has no UI for certain event types (e.g. a sharing
   * surface that hides tool-call activity).
   */
  ignoreEventTypes?: Array<ChatStreamEvent['type']>;
}

export interface TurnOrchestratorState {
  messages: ChatMessage[];
  isStreaming: boolean;
  isAwaitingResponse: boolean;
  error: string | null;
}

export interface TurnOrchestrator extends TurnOrchestratorState {
  turn: TurnState;
  getTurn: () => TurnState;
  dispatch: (action: TurnAction) => void;
  /** Feed a mapped event into the turn, in seq order */
  pushEvent: (event: ChatStreamEvent, uuid?: string) => void;

  /** Record one subscription payload, whether or not it carried events. */
  markPayload: () => void;

  /** Read contiguous progress without subscribing to per-event React updates. */
  getStreamProgress: () => StreamBufferProgress;

  /** Add a user message. Returns its generated ID (for removeMessage on failure). */
  addUserMessage: (
    message: Pick<ChatMessage, 'content' | 'attachments'>,
  ) => string;

  /** Remove a message by ID (e.g. on mutation failure) */
  removeMessage: (id: string) => void;

  /** Mark stream as active and reset all accumulator state */
  startStreaming: () => void;

  /** Abort the stream, remove any in-progress message. Optionally set an error. */
  abort: (errorMessage?: string) => void;

  /** Full reset — clear messages, errors, stream state */
  resetAll: () => void;

  /**
   * Replace settled messages with the server record, keeping what the running
   * turn has added, including settled server messages absent from a stale fetch.
   */
  hydrateMessages: (messages: ChatMessage[]) => void;
}

function createTurnStore(initial: TurnState) {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispatch: (action: TurnAction) => {
      const result = reduceTurn(state, action);
      state = result.state;
      listeners.forEach((listener) => listener());
      return result.events;
    },
  };
}

export function useTurnOrchestrator(
  options?: UseTurnOrchestratorOptions,
): TurnOrchestrator {
  const callbacks = useRef(options);
  callbacks.current = options;
  const [store] = useState(() =>
    createTurnStore(
      initialTurnState(options?.initialMessages, options?.initialChatId),
    ),
  );
  const turn = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const toolArguments = useRef(new ToolCallStreamAccumulator());
  const transport = useRef({
    payloadCount: 0,
    gapSince: null as number | null,
    lastProgressAt: null as number | null,
  });
  const counter = useRef(0);
  const dispatch = useCallback(
    (action: TurnAction) => {
      const before = store.getSnapshot();
      const events = store.dispatch(action);
      const after = store.getSnapshot();
      if (!before.notifiedStart && after.notifiedStart) {
        callbacks.current?.onStreamStart?.();
      }
      if (after.generation !== before.generation) {
        toolArguments.current.reset();
        transport.current = {
          payloadCount: 0,
          gapSince: null,
          lastProgressAt: null,
        };
      }
      if (action.type === 'SERVER_EVENT') {
        const t = transport.current;
        if (after.nextSeq !== before.nextSeq) {
          t.lastProgressAt = Date.now();
        }
        // Only advancing the cursor gives a new missing seq its own grace period.
        if (after.parked.size === 0) {
          t.gapSince = null;
        } else if (after.nextSeq !== before.nextSeq || t.gapSince === null) {
          t.gapSince = Date.now();
        }
      }
      for (const event of events) {
        if (event.type === 'TOOL_CALL_EXECUTE') {
          callbacks.current?.onToolExecute?.(event);
        }
        if (event.type === 'TOOL_CALL_DELTA') {
          callbacks.current?.onToolStreaming?.(
            toolArguments.current.processArgumentDeltas(
              event.toolCallId,
              event.toolName,
              event.argumentDeltas ?? [],
            ),
          );
        }
        if (event.type === 'TOOL_CALL_RESULT') {
          callbacks.current?.onToolResult?.({
            ...event,
            result: event.result ?? null,
          });
        }
        if (event.type === 'MESSAGE_COMPLETE') {
          toolArguments.current.reset();
        }
        if (
          event.type === 'DONE' ||
          event.type === 'ERROR' ||
          event.type === 'CANCELLED'
        ) {
          callbacks.current?.onStreamEnd?.(event.type);
        }
      }
    },
    [store],
  );
  const pushEvent = useCallback(
    (event: ChatStreamEvent, uuid?: string) =>
      dispatch({
        type: 'SERVER_EVENT',
        event,
        uuid,
        ignore: callbacks.current?.ignoreEventTypes,
      }),
    [dispatch],
  );
  const addUserMessage = useCallback(
    (message: Pick<ChatMessage, 'content' | 'attachments'>) => {
      const id = `temp-${++counter.current}`;
      dispatch({type: 'ADD', message: {...message, id, role: 'user'}});
      return id;
    },
    [dispatch],
  );
  const removeMessage = useCallback(
    (id: string) => dispatch({type: 'REMOVE', id}),
    [dispatch],
  );
  const startStreaming = useCallback(
    () => dispatch({type: 'START'}),
    [dispatch],
  );
  const abort = useCallback(
    (error?: string) => dispatch({type: 'ABORT', error}),
    [dispatch],
  );
  const resetAll = useCallback(() => dispatch({type: 'SWITCH'}), [dispatch]);
  const markPayload = useCallback(() => {
    transport.current.payloadCount += 1;
  }, []);
  const getStreamProgress = useCallback((): StreamBufferProgress => {
    const {nextSeq, phase} = store.getSnapshot();
    return {...transport.current, nextSeq, terminal: phase === 'idle'};
  }, [store]);
  const hydrateMessages = useCallback(
    (messages: ChatMessage[]) => dispatch({type: 'HYDRATE', messages}),
    [dispatch],
  );

  const streaming = turn.messages.at(-1)?.streaming
    ? turn.messages.at(-1)
    : undefined;
  const isStreaming = turn.phase !== 'idle';
  return {
    turn,
    dispatch,
    getTurn: store.getSnapshot,
    messages: turn.messages,
    error: turn.error,
    isStreaming,
    isAwaitingResponse:
      isStreaming &&
      !streaming?.content &&
      !streaming?.toolCalls?.some((t) => t.intent || t.name),
    pushEvent,
    markPayload,
    getStreamProgress,
    addUserMessage,
    removeMessage,
    startStreaming,
    abort,
    resetAll,
    hydrateMessages,
  };
}
